import type { ComponentProps } from "react"
import { format } from "date-fns"
import { formatNumber } from "@/v2-common/utils/utils.formatting"
import cn from "@/v2-ui/utils/utils.cn"
import Tooltip from "@/v2-ui/tooltip"
import type { UsageDailyTotal } from "@/v2-backoffice-client/usage/usage.utils"

export type UsageHistoryStripProps = ComponentProps<"div"> & {
  /** Zero-filled daily totals, oldest first (padDailyTotals output). */
  entries: UsageDailyTotal[]
}

// Uptime-history-bar sibling for the usage panels: one cell per day with a
// real (Radix) tooltip carrying the date + weighted/raw numbers. Intensity
// is the day's share of the busiest day in the window. Panel-only — table
// rows keep the cheap native-title UsageDayStrip.
function UsageHistoryStrip(props: UsageHistoryStripProps) {
  const { entries, className, ...divProps } = props

  const max = Math.max(1, ...entries.map((entry) => entry.value))

  return (
    <div
      className={cn("usage-history-strip", className)}
      role="img"
      aria-label={`${entries.length}-day activity history`}
      {...divProps}
    >
      {entries.map((entry) => {
        const share = entry.value / max
        const variant =
          entry.value <= 0
            ? "empty"
            : share <= 0.33
              ? "low"
              : share <= 0.66
                ? "mid"
                : "high"
        const label =
          entry.value > 0
            ? `${format(new Date(entry.day), "d. MMM")}: ${formatNumber(Math.round(entry.value))} weighted activity (${formatNumber(entry.rawCount)} events)`
            : `${format(new Date(entry.day), "d. MMM")}: no activity`

        return (
          <Tooltip key={entry.day}>
            <Tooltip.Trigger asChild>
              <div
                className={cn(
                  "usage-history-strip-cell",
                  `usage-history-strip-cell-${variant}`,
                )}
              />
            </Tooltip.Trigger>
            <Tooltip.Content side="top">{label}</Tooltip.Content>
          </Tooltip>
        )
      })}
    </div>
  )
}

export default UsageHistoryStrip
